/**
 * A curated eval tag (the `eval.tag` entity, backend issue #91). Tags are
 * assigned to cases as entity-level labels and drive run selection; deletion is
 * a soft-delete on the backend, so a removed tag simply leaves the active list.
 */
export type Tag = {
  tag_id: string
  name: string
  description: string | null
  created_at: string
}

export type TagListResponse = {
  tags: Tag[]
  total: number
  limit: number
  offset: number
}

/** Page size for the server-side paginated tag list (backend caps limit at 200). */
export const TAGS_PAGE_SIZE = 25

export type TagFilters = {
  /** Case-insensitive name search; sent to the backend as `q`. */
  q?: string
  page: number
  pageSize: number
}

export const getDefaultTagFilters = (): TagFilters => ({
  page: 1,
  pageSize: TAGS_PAGE_SIZE,
})

/** Build the query string for the list endpoint: name search + pagination. */
export const buildTagsListQuery = (filters: TagFilters): URLSearchParams => {
  const query = new URLSearchParams()
  const q = filters.q?.trim()
  if (q) {
    query.set('q', q)
  }
  query.set('limit', String(filters.pageSize))
  query.set('offset', String((filters.page - 1) * filters.pageSize))
  return query
}

export type TagCreateInput = {
  name: string
  description: string | null
}

/** PATCH payload: omitted fields are unchanged, but the editor always sends both. */
export type TagUpdateInput = {
  tag_id: string
  name: string
  description: string | null
}

/** A blank description is sent as null (clears it) rather than an empty string. */
const normalizeDescription = (description: string): string | null => {
  const trimmed = description.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * Pre-flight the backend's name rule: a tag name must be non-empty (the
 * backend also rejects a duplicate active name with 409, surfaced via detail).
 */
export const validateTagName = (name: string): string | null => {
  if (!name.trim()) {
    return 'Name is required.'
  }
  return null
}

export const buildTagCreateInput = (
  name: string,
  description: string,
): TagCreateInput => ({
  name: name.trim(),
  description: normalizeDescription(description),
})

/**
 * Build the PATCH payload for editing a tag. Unlike rubrics, a tag can be
 * renamed, so both the name and description are editable.
 */
export const buildTagUpdateInput = (
  tag: Tag,
  name: string,
  description: string,
): TagUpdateInput => ({
  tag_id: tag.tag_id,
  name: name.trim(),
  description: normalizeDescription(description),
})
